import getConfig from 'next/config'
import type { PostHog } from 'posthog-js'
import { filterCapturedNetworkRequest } from './networkCapture'

const DEFAULT_API_HOST = 'https://us.i.posthog.com'

/**
 * posthog-js is ~270kB, so it is imported dynamically rather than bundled into
 * `_app` and shipped to every page. Calls made while it loads are queued; this
 * caps how many, so a stalled load can't grow the queue without bound.
 */
const MAX_PENDING_OPERATIONS = 100

/**
 * Every exported function below is a no-op on the server. `usePostHogTracking`
 * is called from `_app`, so it runs during SSR too - without this guard the
 * dedupe state (`registeredContext`, `identifiedUserId`) would be mutated in a
 * Node process where module state is shared across every request.
 */
const isBrowser = () => typeof window !== 'undefined'

/**
 * Properties PostHog stamps on every event that carry the full page URL. The
 * checkout and cart URLs hold the encrypted `order` / `paygates` blobs the
 * mobile app hands over, plus `uid`, so the query string is dropped before
 * anything leaves the browser.
 */
const URL_PROPERTIES = ['$current_url', '$referrer', '$initial_current_url', '$initial_referrer']

function stripQueryString(value: unknown) {
  if (typeof value !== 'string') return value
  const queryStart = value.indexOf('?')
  return queryStart === -1 ? value : value.slice(0, queryStart)
}

type PostHogOperation = (client: PostHog) => void

let client: PostHog | null = null
let loading = false

/**
 * Two queues, drained configuration-first.
 *
 * `usePostHogTracking` runs in `_app`, and React fires a child's effects *before*
 * its parent's - so a page's `cart_page_loaded` is captured before `_app` has had
 * a chance to register identity. Draining configuration ahead of events makes the
 * outcome independent of that ordering, which matters because
 * `person_profiles: 'identified_only'` does not retro-attach events captured
 * before `identify`.
 *
 * Operations are buffered from the first call, not just once loading has started,
 * so nothing is dropped in the window before `initPostHog` runs.
 */
const pendingConfig: PostHogOperation[] = []
const pendingEvents: PostHogOperation[] = []

function enqueue(queue: PostHogOperation[], operation: PostHogOperation) {
  if (client) {
    operation(client)
    return
  }
  if (queue.length < MAX_PENDING_OPERATIONS) {
    queue.push(operation)
  }
}

function drainInto(posthog: PostHog) {
  // splice(0) empties in place, so a queued operation that itself enqueues
  // cannot re-run the batch it came from.
  pendingConfig.splice(0).forEach((operation) => operation(posthog))
  pendingEvents.splice(0).forEach((operation) => operation(posthog))
}

/**
 * Starts loading posthog-js. Safe to call repeatedly and on every render - only
 * the first call on the client with a configured key does anything.
 */
export function initPostHog() {
  if (!isBrowser() || client || loading) return

  const { posthogKey, posthogHost, env, buildVersion } = getConfig().publicRuntimeConfig
  if (!posthogKey) return

  loading = true

  import('posthog-js')
    .then(({ default: posthog }) => {
      posthog.init(posthogKey, {
        api_host: posthogHost || DEFAULT_API_HOST,
        // Checkout and cart are single views inside a webview, so `$pageview`
        // is captured explicitly by usePostHogTracking, not on history changes.
        capture_pageview: false,
        capture_pageleave: true,
        // The Stripe card fields are iframes, but the address form is ours -
        // autocapture would read what the customer types into it.
        autocapture: false,
        disable_session_recording: false,
        session_recording: {
          // Defence in depth - the project enforces this server-side too. Every
          // input value (email, phone, address, name) is replaced with asterisks
          // before the recording leaves the browser.
          maskAllInputs: true,
          // Keeps rrweb out of cross-origin iframes, which is what the Stripe card
          // fields are. This is the one setting standing between replay and card
          // input, so change it deliberately or not at all.
          recordCrossOriginIframes: false,
          // Escape hatch for anything that renders customer data as text rather
          // than as an input value - see the note in docs/event-tracking.md.
          maskTextSelector: '[data-ph-mask]',
          // Request/response capture. Payloads are filtered in the browser by
          // filterCapturedNetworkRequest before they ever reach PostHog.
          recordHeaders: true,
          recordBody: true,
          // Read bodies through a streaming reader that stops at the size limit,
          // rather than buffering a large response on a checkout page.
          streamNetworkBody: true,
          maskCapturedNetworkRequestFn: filterCapturedNetworkRequest
        },
        // Surfaces the network tab in replay; without this there are no timings
        // to hang the captured requests off.
        capture_performance: { network_timing: true },
        persistence: 'localStorage+cookie',
        // Anonymous carts still send events; they just don't get a person profile.
        person_profiles: 'identified_only',
        sanitize_properties: (properties) => {
          const sanitized = { ...properties }
          URL_PROPERTIES.forEach((key) => {
            if (key in sanitized) sanitized[key] = stripQueryString(sanitized[key])
          })
          return sanitized
        }
      })
      posthog.register({ env, build_version: buildVersion })

      // posthog-js does not attach itself to `window` when installed from npm,
      // which leaves no way to inspect it from the console. Expose it outside
      // production so `__posthog.capture('test')` is a one-liner when events
      // aren't showing up.
      if (env !== 'prd') {
        ;((window as unknown) as Record<string, unknown>).__posthog = posthog
      }

      client = posthog
      drainInto(posthog)
    })
    .catch((error) => {
      loading = false
      pendingConfig.length = 0
      pendingEvents.length = 0
      console.error('Init PostHog error', error)
    })
}

let registeredContext = ''

/**
 * Every super property this app has ever owned, **including retired ones**.
 *
 * `register` merges into localStorage-backed persistence that outlives both the
 * page load and the PostHog session, and `init` reads it back. So a key dropped
 * from the context does not disappear - it keeps riding on every future event
 * from any browser that ever saw the older build. `sellpage_slug` is exactly
 * that case: it was registered by an earlier build and is no longer sent.
 *
 * Retire a name by leaving it in this list, never by deleting it - same
 * convention as the `compressUrl` mapping table.
 */
const OWNED_CONTEXT_KEYS = [
  'session_id',
  'sellpage_id',
  'sellpage_slug',
  'page_version',
  'checkout_source',
  'app_build',
  'cart_id'
]

/**
 * Registers super properties attached to every subsequent event, and clears any
 * owned key the current context no longer sends. Deduplicated so a re-render
 * with an unchanged context doesn't rewrite persistence.
 */
export function registerPostHogContext(properties: Record<string, unknown>) {
  if (!isBrowser()) return

  const fingerprint = JSON.stringify(properties)
  if (fingerprint === registeredContext) return

  registeredContext = fingerprint
  const retired = OWNED_CONTEXT_KEYS.filter((key) => !(key in properties))

  enqueue(pendingConfig, (posthog) => {
    // The instance-level `unregister` takes one key at a time.
    retired.forEach((key) => posthog.unregister(key))
    posthog.register(properties)
  })
}

let identifiedUserId = ''

export function identifyPostHog(userId: string, properties?: Record<string, unknown>) {
  if (!isBrowser() || !userId || userId === identifiedUserId) return

  identifiedUserId = userId
  enqueue(pendingConfig, (posthog) => posthog.identify(userId, properties))
}

export function capturePostHogEvent(name: string, properties?: Record<string, unknown>) {
  if (!isBrowser()) return
  enqueue(pendingEvents, (posthog) => posthog.capture(name, properties))
}
